'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useGetConversationsQuery } from '@/api/features/conversations';
import { reduxLogger } from '@/core/utils/logger';
import { useTour } from '../hooks/useTour';
import { useCompleteTourWithTracking } from '../hooks/useCompleteTourWithTracking';
import { useTourAutoAdvance } from '../hooks/useTourAutoAdvance';
import { TourHighlight } from '../components/TourHighlight';
import { LoggingWelcomeModal } from './LoggingWelcomeModal';
import { LoggingCompleteModal } from './LoggingCompleteModal';
import { LOGGING_TOUR_STEPS } from './types';

/** Count of highlight-type steps (for "Step X of Y" display) */
const HIGHLIGHT_STEPS = LOGGING_TOUR_STEPS.filter((s) => s.type === 'highlight');

/**
 * LoggingTourOverlay - Renders the Logging tour step UI.
 *
 * Only renders when the active tour is 'logging'. Navigates the user
 * to the Voley Support conversation for the manual logging demo,
 * then to Settings for the auto-logging configuration walkthrough.
 */
export function LoggingTourOverlay() {
  const router = useRouter();
  const { isActive, activeTourId, currentStep, nextStep } = useTour();
  const { completeTourWithTracking } = useCompleteTourWithTracking();

  const isLoggingTour = activeTourId === 'logging';

  // Auto-advance polling
  useTourAutoAdvance(isLoggingTour, currentStep, nextStep);

  // Find the Voley Support conversation for navigation
  const { data: searchResults } = useGetConversationsQuery(
    { search: 'Voley Support', limit: 1 },
    { skip: !isLoggingTour, refetchOnMountOrArgChange: 5 * 60 },
  );
  const voleySupportId = searchResults?.items?.[0]?.id;

  // Track which steps we've already navigated for (to avoid re-navigating)
  const navigatedForStepRef = useRef<string | null>(null);

  // Handle step-specific navigation explicitly in the overlay
  useEffect(() => {
    if (!isLoggingTour || !currentStep) return;
    if (navigatedForStepRef.current === currentStep.id) return;

    // Steps that need Voley Support conversation open
    const stepsNeedingConversation = [
      'highlight_log_btn',
      'highlight_logging_panel',
      'highlight_log_selected',
      'highlight_text_posting',
      'highlight_link_posting',
      'highlight_tasks_mms',
    ];
    if (stepsNeedingConversation.includes(currentStep.id) && voleySupportId) {
      navigatedForStepRef.current = currentStep.id;
      router.push(`/dashboard?view=all&conversation=${voleySupportId}`);
      reduxLogger.info(`[LoggingTour] Navigating to Voley Support for step: ${currentStep.id}`);
      return;
    }

    // Navigate to Settings for the auto-logging steps
    const settingsSteps = ['highlight_log_on_close', 'highlight_end_of_day'];
    if (settingsSteps.includes(currentStep.id)) {
      navigatedForStepRef.current = currentStep.id;
      router.push('/settings?section=logging');
      reduxLogger.info(`[LoggingTour] Navigating to Settings > Logging for step: ${currentStep.id}`);
    }
  }, [isLoggingTour, currentStep?.id, currentStep, voleySupportId, router]);

  const handleWelcomeComplete = useCallback(() => {
    if (voleySupportId) {
      router.push(`/dashboard?view=all&conversation=${voleySupportId}`);
      reduxLogger.info('[LoggingTour] Navigating to Voley Support conversation');
    } else {
      router.push('/dashboard?view=all');
      reduxLogger.warn('[LoggingTour] Voley Support conversation not found, navigating to dashboard');
    }
    nextStep();
  }, [voleySupportId, router, nextStep]);

  const handleFinish = useCallback(() => {
    completeTourWithTracking('logging');
    router.push('/dashboard?view=all');
  }, [completeTourWithTracking, router]);

  const handleHighlightNext = useCallback(() => {
    nextStep();
  }, [nextStep]);

  if (!isLoggingTour || !isActive || !currentStep) return null;

  // Backdrop visibility — block clicks only for non-interactive, non-auto-advancing highlight steps
  const isHighlightStep = currentStep.type === 'highlight';
  const isClickThrough = !!(
    currentStep.advanceOnNavigate ||
    currentStep.advanceOnElement ||
    currentStep.advanceOnElementRemoved
  );
  const isInteractive = !!currentStep.allowInteraction;
  const showBackdrop = isHighlightStep && !isClickThrough && !isInteractive;

  return (
    <>
      {/* Persistent backdrop */}
      <div
        className="fixed inset-0 transition-opacity duration-200"
        style={{
          zIndex: 199,
          pointerEvents: showBackdrop ? 'auto' : 'none',
          opacity: showBackdrop ? 1 : 0,
        }}
      />

      {/* Step content */}
      {currentStep.type === 'modal' && (
        <>
          {currentStep.id === 'logging_welcome' && <LoggingWelcomeModal onComplete={handleWelcomeComplete} />}
          {currentStep.id === 'logging_complete' && <LoggingCompleteModal onFinish={handleFinish} />}
        </>
      )}

      {currentStep.type === 'highlight' &&
        (() => {
          const highlightIndex = HIGHLIGHT_STEPS.findIndex((s) => s.id === currentStep.id);
          return (
            <TourHighlight
              step={currentStep}
              stepNumber={highlightIndex + 1}
              totalSteps={HIGHLIGHT_STEPS.length}
              onNext={handleHighlightNext}
              isLastStep={highlightIndex === HIGHLIGHT_STEPS.length - 1}
            />
          );
        })()}
    </>
  );
}
